import type { GraphNodeDatum } from './graphTransforms'

/**
 * Canvas can't resolve Chakra tokens, so colors here are hex. Mime fallbacks
 * mirror getFileIconColor() in FolderTree (red/green/purple/gray at the 500 step).
 */
const MIME_COLORS = {
  pdf: '#e53e3e',
  sheet: '#38a169',
  image: '#805ad5',
  doc: '#3182ce',
  default: '#718096',
} as const

/** Chakra color-scheme names a tag may carry, at the 500 step. */
const SCHEME_HEX: Record<string, string> = {
  gray: '#718096',
  red: '#e53e3e',
  orange: '#dd6b20',
  yellow: '#d69e2e',
  green: '#38a169',
  teal: '#319795',
  blue: '#3182ce',
  cyan: '#00b5d8',
  purple: '#805ad5',
  pink: '#d53f8c',
  brand: '#2196f3',
}

export const BRAND_HEX = '#2196f3'

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

export function mimeFamily(mime: string | null | undefined): 'pdf' | 'sheet' | 'image' | 'doc' | 'default' {
  if (!mime) return 'default'
  if (mime === 'application/pdf') return 'pdf'
  if (mime.includes('sheet') || mime.includes('excel')) return 'sheet'
  if (mime.startsWith('image/')) return 'image'
  if (mime.includes('word') || mime.includes('document')) return 'doc'
  return 'default'
}

export function mimeColor(mime: string | null | undefined): string {
  return MIME_COLORS[mimeFamily(mime)]
}

/** Short glyph painted inside the node in place of an icon. */
export function mimeGlyph(mime: string | null | undefined): string {
  switch (mimeFamily(mime)) {
    case 'pdf':
      return 'PDF'
    case 'sheet':
      return 'XLS'
    case 'image':
      return 'IMG'
    case 'doc':
      return 'DOC'
    default:
      return 'TXT'
  }
}

/** First tag with a usable color wins; otherwise the mime family color. */
export function resolveNodeColor(node: Pick<GraphNodeDatum, 'tags' | 'mime_type'>): string {
  for (const tag of node.tags) {
    const c = tag.color?.trim()
    if (!c) continue
    if (HEX_RE.test(c)) return c
    const scheme = SCHEME_HEX[c.toLowerCase().replace(/\.\d+$/, '')]
    if (scheme) return scheme
  }
  return mimeColor(node.mime_type)
}

export function truncateLabel(name: string, max = 24): string {
  return name.length > max ? `${name.slice(0, max - 1)}…` : name
}
